/**
 * ActivitiesDashboard - Página do Dashboard de Atividades
 *
 * Reutiliza o sistema unificado de dashboards (`useModuleDashboards`
 * + `DashboardRenderer`) já adotado em CRM/Tasks/Financial.
 *
 * Os dashboards exibidos vêm da tabela `dashboards` filtrados por
 * `module = 'activities'`. Atualmente o único dashboard registrado
 * neste módulo é o `crm_playbook_overview` (Visão Geral Playbooks).
 *
 * @version 1.0.0
 * RELEASE: 2026-05-08 v1.0.0 - Criação inicial (Fase: dashboard atividades)
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, LayoutDashboard, RefreshCw } from 'lucide-react';
import { useModuleDashboards } from '@/hooks/useDashboards';
import { DashboardTabs, DashboardRenderer } from '@/components/crm/dashboard';
import { Button } from '@/components/ui/button';
import { useRefreshWithTimestamp } from '@/hooks/useRefreshWithTimestamp';
import { cn } from '@/lib/utils';

/**
 * Página principal do Dashboard de Atividades.
 * Renderiza dinamicamente o dashboard selecionado via DashboardRegistry.
 */
export default function ActivitiesDashboard() {
  const queryClient = useQueryClient();
  const { isRefreshing, triggerRefresh } = useRefreshWithTimestamp();

  // Hook unificado: lista todos os dashboards do módulo "activities"
  const {
    dashboards,
    selectedDashboard,
    selectedDashboardId,
    selectDashboard,
    isLoading,
  } = useModuleDashboards('activities');

  /** Atualiza dados invalidando caches de queries dos dashboards */
  const handleRefresh = useCallback(async () => {
    await triggerRefresh(async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['playbook-overview'] }),
        queryClient.invalidateQueries({ queryKey: ['crm-playbook-overview'] }),
        queryClient.invalidateQueries({ queryKey: ['my-activities-overview'] }),
      ]);
    });
  }, [queryClient, triggerRefresh]);

  // Loading inicial
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Estado vazio (sem dashboards liberados via permissões)
  if (dashboards.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <LayoutDashboard className="h-16 w-16 text-muted-foreground/50" />
        <h1 className="text-2xl font-bold text-foreground">Dashboard de Atividades</h1>
        <p className="text-muted-foreground text-center max-w-md">
          Nenhum dashboard disponível para este módulo. Verifique as permissões
          de acesso na tela de Permissões de Dashboard.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header (título + refresh + slot p/ filtros do dashboard ativo) */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 shrink-0">
          <h1 className="text-lg md:text-2xl font-bold tracking-tight text-foreground">
            Dashboard de Atividades
          </h1>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isRefreshing && 'animate-spin')} />
          </Button>
        </div>

        {/* Separador visual */}
        <span className="text-muted-foreground/30 shrink-0">|</span>

        {/* Slot DOM onde cada dashboard injeta sua própria barra de filtros
            via React Portal (ex.: TeamActivitiesDashboard usa este slot). */}
        <div id="activities-header-filters-slot" className="flex items-center gap-1.5 min-w-0 flex-1" />
      </div>

      {/* Abas (quando houver mais de 1 dashboard registrado neste módulo) */}
      {dashboards.length > 1 && (
        <DashboardTabs
          dashboards={dashboards}
          selectedDashboardId={selectedDashboardId}
          onSelectDashboard={selectDashboard}
        />
      )}

      {/* Renderização dinâmica via DashboardRegistry */}
      {selectedDashboard && (
        <DashboardRenderer
          componentKey={selectedDashboard.component_key}
          dashboardId={selectedDashboard.id}
        />
      )}
    </div>
  );
}
